import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WhistleBlower Admin Queue — Founder-only review page.
 *
 * Founder ruling 2026-08-11:
 *   "I think I need to be a catch-all on the whistleblowers before they go
 *   live. Final Founder approval after AI triage approval... Build the admin
 *   page that lists queued Whistleblower filings so you can approve them
 *   from your phone with one tap."
 *
 * Design:
 *   - Mobile-first · single-column · big touch targets
 *   - Master-token gate · token pasted once, cached in sessionStorage
 *   - Each filing shows story, AI verdicts, and 3 big buttons: AMPLIFY / HOLD / REJECT
 *   - One-tap approve · zero navigation friction
 */
export default function WhistleblowerAdminPage() {
  const [token, setToken] = useState(() => {
    try {
      return sessionStorage.getItem("sage_master_token") || "";
    } catch {
      return "";
    }
  });
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState("");
  const [busyTicket, setBusyTicket] = useState(null);

  const loadQueue = async (tok) => {
    setError("");
    try {
      const r = await fetch(`${API}/api/sage/whistleblower/queue`, {
        headers: { "X-Sage-Master": tok || token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "not authorized");
      setQueue(d);
    } catch (e) {
      setError(String(e.message || e));
      setQueue(null);
    }
  };

  useEffect(() => {
    if (token) loadQueue(token);
  }, [token]);

  const submitToken = () => {
    try {
      sessionStorage.setItem("sage_master_token", token);
    } catch {}
    loadQueue(token);
  };

  const decide = async (ticket, verdict) => {
    setBusyTicket(ticket);
    try {
      const r = await fetch(`${API}/api/sage/whistleblower/review/${ticket}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Sage-Master": token },
        body: JSON.stringify({ founder_verdict: verdict }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert("Decision failed: " + (d.detail || r.status));
      }
      await loadQueue(token);
    } finally {
      setBusyTicket(null);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative" data-testid="whistleblower-admin-page">
      <TopNav />
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Founder-only · review queue</div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase mb-4">
          WhistleBlower · Queue
        </h1>
        <p className="text-sm md:text-base text-sage-muted sage-prose mb-6">
          Tap AMPLIFY to send to the Auto-Scream wall. Tap HOLD to keep in the queue. Tap REJECT to remove.
        </p>

        {/* Master-token gate */}
        {!queue && (
          <div className="border border-sage-accent p-4 mb-6" data-testid="master-token-gate">
            <label className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2 block">
              Sage Master Token
            </label>
            <input
              data-testid="master-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your master token"
              className="w-full border border-sage-border bg-sage-bg px-3 py-3 text-sage-text focus:border-sage-accent outline-none mb-3"
            />
            <button
              onClick={submitToken}
              data-testid="master-token-submit"
              className="w-full border-2 border-sage-accent bg-sage-accent text-sage-bg py-3 text-sm uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text transition-colors"
            >
              Unlock queue
            </button>
            {error && <div className="text-sm text-sage-warn mt-3">{error}</div>}
          </div>
        )}

        {/* Queue summary */}
        {queue && (
          <div className="border border-sage-border p-4 mb-6 grid grid-cols-3 gap-3 text-center" data-testid="queue-summary">
            <div>
              <div className="text-3xl font-bold text-sage-text">{queue.count}</div>
              <div className="text-[10px] uppercase tracking-wider text-sage-muted">Awaiting</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-sage-accent">{queue.ai_all_passed}</div>
              <div className="text-[10px] uppercase tracking-wider text-sage-muted">AI-passed</div>
            </div>
            <div>
              <button
                onClick={() => loadQueue(token)}
                data-testid="refresh-queue"
                className="border border-sage-border text-sage-muted px-3 py-1 text-xs uppercase tracking-wider hover:border-sage-accent hover:text-sage-accent"
              >
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Filings */}
        {queue?.items?.length === 0 && (
          <div className="border border-sage-border p-8 text-center" data-testid="queue-empty">
            <div className="text-sm text-sage-muted italic mb-2">The queue is empty.</div>
            <div className="text-xs text-sage-faint">
              When strangers file, they land here. Cluster of 3 AI reviews · then you.
            </div>
          </div>
        )}

        {queue?.items?.map((f) => (
          <FilingCard
            key={f.ticket}
            filing={f}
            decide={decide}
            busy={busyTicket === f.ticket}
          />
        ))}

        <div className="mt-10 text-xs text-sage-muted italic">
          <Link to="/whistleblower" className="text-sage-accent underline">Back to public WhistleBlower</Link>
        </div>
      </div>
    </div>
  );
}

function Verdict({ label, value }) {
  const color = value === "pass" ? "sage-accent" : value === "fail" ? "sage-warn" : "sage-muted";
  const dot = value === "pass" ? "●" : value === "fail" ? "✗" : "○";
  return (
    <div className={`text-[10px] uppercase tracking-wider text-${color}`}>
      <span className="mr-1">{dot}</span>
      {label}: {value || "pending"}
    </div>
  );
}

function FilingCard({ filing, decide, busy }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-2 border-sage-border mb-4 p-4" data-testid={`filing-${filing.ticket}`}>
      <div className="flex justify-between items-start mb-3 gap-3">
        <div>
          <div className="text-lg font-bold uppercase text-sage-text">
            {filing.industry || "Industry undisclosed"}
          </div>
          {filing.target && (
            <div className="text-xs text-sage-muted italic mt-1">{filing.target}</div>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-sage-faint text-right shrink-0">
          {(filing.filed_at || "").slice(0, 10)}<br />
          <span className="opacity-70">{filing.ticket.slice(0, 8)}…</span>
        </div>
      </div>

      {/* AI verdict row */}
      <div className="flex flex-wrap gap-3 mb-3 pb-3 border-b border-sage-border" data-testid={`verdicts-${filing.ticket}`}>
        <Verdict label="Sage" value={filing.sage_verdict} />
        <Verdict label="Warden" value={filing.warden_verdict} />
        <Verdict label="Verifier" value={filing.verifier_verdict} />
      </div>

      {/* Story · collapsible */}
      <div className={`text-sm text-sage-text sage-prose mb-3 whitespace-pre-wrap ${expanded ? "" : "line-clamp-4"}`}>
        {filing.story}
      </div>
      {filing.story?.length > 200 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-sage-accent underline mb-3"
          data-testid={`toggle-${filing.ticket}`}
        >
          {expanded ? "Collapse" : "Read full"}
        </button>
      )}

      {filing.evidence && expanded && (
        <div className="text-xs text-sage-muted italic border-l-2 border-sage-border pl-3 mb-3">
          <strong>Evidence:</strong> {filing.evidence}
        </div>
      )}

      {/* One-tap decision row */}
      <div className="grid grid-cols-3 gap-2 mt-4" data-testid={`decision-${filing.ticket}`}>
        <button
          onClick={() => decide(filing.ticket, "amplify")}
          disabled={busy}
          data-testid={`amplify-${filing.ticket}`}
          className="border-2 border-sage-accent bg-sage-accent text-sage-bg py-3 text-xs uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
        >
          Amplify
        </button>
        <button
          onClick={() => decide(filing.ticket, "hold")}
          disabled={busy}
          data-testid={`hold-${filing.ticket}`}
          className="border-2 border-sage-border text-sage-muted py-3 text-xs uppercase tracking-wider font-bold hover:border-sage-text hover:text-sage-text disabled:opacity-50 transition-colors"
        >
          Hold
        </button>
        <button
          onClick={() => decide(filing.ticket, "reject")}
          disabled={busy}
          data-testid={`reject-${filing.ticket}`}
          className="border-2 border-sage-warn text-sage-warn py-3 text-xs uppercase tracking-wider font-bold hover:bg-sage-warn hover:text-sage-bg disabled:opacity-50 transition-colors"
        >
          Reject
        </button>
      </div>
    </div>
  );
}
